import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import emailjs from "@emailjs/browser";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { MdDateRange } from "react-icons/md";

const RentalFormSection = ({
  activeTab,
  selectedDates,
  formData,
  setFormData,
  fetchBookedEvents,
  setSelectedDates,
}) => {
  const [loading, setLoading] = useState(false);

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };


  const sendEmailNotification = async (bookingData) => {
  // console.log('bookingData', formData)

    try {
      // Prepare template parameters
      const templateParams = {
        eventType: bookingData.eventType,
        // field: bookingData.field,
        teamOne: bookingData.teamOne || "N/A",
        teamTwo: bookingData.teamTwo || "N/A",
        numberOfMatches: bookingData.numberOfMatches || "N/A",
        numberOfTeams: bookingData.numberOfTeams || "N/A",
        startDate: selectedDates[0] || "N/A",
        endDate: selectedDates[1] || selectedDates[0] || "N/A",
        name: bookingData.bookedBy.name,
        email: bookingData.bookedBy.email,
        phone: bookingData.bookedBy.phone,
        role: bookingData.bookedBy.role,
        notes: bookingData.notes || "No additional notes",
      };

      // Send email using EmailJS
      const response = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams
      );

      console.log("Email sent successfully:", response);
      return true;
    } catch (error) {
      console.error("Error sending email:", error);
      // Don't throw error - we don't want to block the booking success message
      // Just log it and continue
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedDates.length === 0) {
      toast("Please select at least one date from the calendar.", {
        position: "top-right",
        autoClose: 5000,
        style: {
          background: "linear-gradient(135deg, #FF8A00, #FF3D00)",
          color: "#fff",
          fontWeight: "500",
          fontFamily: "'Poppins', sans-serif",
          borderRadius: "12px",
          border: "1px solid #ff660040",
          boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
        },
        icon: <FaCalendarAlt size={20} color="#fff" />,
      });
      return;
    }
    if (activeTab === "tournament" && selectedDates.length !== 2) {
      toast(
        "Please select exactly two dates (start and end) for a tournament.",
        {
          position: "top-right",
          autoClose: 5000,
          style: {
            background: "linear-gradient(135deg, #FF8A00, #FF3D00)",
            color: "#fff",
            fontWeight: "500",
            fontFamily: "'Poppins', sans-serif",
            borderRadius: "12px",
            border: "1px solid #ff660040",
            boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
          },
          icon: <MdDateRange size={36} color="#fff" />,
        }
      );

      return;
    }

    let submissionData = {
      eventType: activeTab,
      field: formData.field,
      bookedBy: formData.bookedBy,
      notes: formData.notes,
      status: "pending",
    };

    if (activeTab === "single") {
      submissionData.teamOne = formData.teamOne;
      submissionData.teamTwo = formData.teamTwo;
      submissionData.matchDate = selectedDates[0];
    } else if (activeTab === "tournament") {
      submissionData.numberOfMatches = formData.numberOfMatches;
      submissionData.numberOfTeams = formData.numberOfTeams;
      submissionData.dateRange = {
        start: selectedDates[0],
        end: selectedDates[1],
      };
    }

    try {
      setLoading(true);
      
      // Submit booking to backend
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/events/create-event`,
        submissionData,
        { headers: { "Content-Type": "application/json" } }
      );
      
      console.log("Booking Response:", response.data);

      // Send email notification
      const emailSent = await sendEmailNotification(submissionData);
      
      if (!emailSent) {
        console.warn("Booking created but email notification failed");
        // Optional: You could show a different toast message here
      }

      // Reset form
      setSelectedDates([]);
      setFormData({
        eventType: "single",
        field: "",
        teamOne: "",
        teamTwo: "",
        numberOfMatches: "",
        numberOfTeams: "",
        bookedBy: {
          name: "",
          email: "",
          phone: "",
          organization: "",
          address: "",
          role: "",
        },
        notes: "",
      });

      fetchBookedEvents();

      toast.success(
        <div>
          <div>Booking request submitted successfully!</div>
          {emailSent && (
            <div className="text-xs mt-1 opacity-90">
              Confirmation email sent to admin
            </div>
          )}
        </div>,
        {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            background: "linear-gradient(135deg, #050733, #0F9D58)",
            color: "#FFFFFF",
            fontWeight: "600",
            fontFamily: "'Poppins', sans-serif",
            border: "1px solid rgba(205, 27, 27, 0.4)",
            borderRadius: "14px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
            letterSpacing: "0.3px",
          },
          icon: <FaCheckCircle size={22} color="#CD1B1B" />,
        }
      );
    } catch (err) {
      console.error("Error submitting booking:", err.response || err);
      toast(
        err?.response?.data?.error ||
          "Failed to submit booking. Please try again.",
        {
          position: "top-right",
          autoClose: 5000,
          style: {
            background: "linear-gradient(135deg, #FF8A00, #FF3D00)",
            color: "#fff",
            fontWeight: "500",
            fontFamily: "'Poppins', sans-serif",
            borderRadius: "12px",
            border: "1px solid #ff660040",
            boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
          },
          icon: <FaTimesCircle size={20} color="#fff" />,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full lg:w-3/5 flex justify-center">
      <div className="bg-gradient-to-br from-[#150A2A] to-[#1E1235] p-6 md:p-8 rounded-2xl shadow-2xl w-full h-full flex flex-col">
        <div className="text-center mb-6">
          <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-red-400 to-purple-400 bg-clip-text text-transparent">
            Schedule Your Rental
          </h2>
          <p className="text-gray-300 mt-2 text-sm">
            Fill out your details below. We'll reach out to confirm your
            booking.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div className="space-y-6 flex-1">
            {/* Dynamic Fields */}
            {activeTab === "single" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Team One
                  </label>
                  <input
                    type="text"
                    name="teamOne"
                    value={formData.teamOne}
                    onChange={handleInputChange}
                    placeholder="Team name"
                    className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Team Two
                  </label>
                  <input
                    type="text"
                    name="teamTwo"
                    value={formData.teamTwo}
                    onChange={handleInputChange}
                    placeholder="Team name"
                    className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
                    required
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Number of Matches
                  </label>
                  <input
                    type="number"
                    name="numberOfMatches"
                    value={formData.numberOfMatches}
                    onChange={handleInputChange}
                    placeholder="e.g., 5"
                    min="1"
                    className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Number of Teams
                  </label>
                  <input
                    type="number"
                    name="numberOfTeams"
                    value={formData.numberOfTeams}
                    onChange={handleInputChange}
                    placeholder="e.g., 8"
                    min="2"
                    className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
                    required
                  />
                </div>
              </div>
            )}

            {/* Personal Info */}
            <div className="pt-4 mt-4 border-t border-gray-700/30">
              <h4 className="text-lg font-semibold mb-4 text-white">
                Your Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="bookedBy.name"
                    value={formData.bookedBy.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Your Role
                  </label>
                  <input
                    type="text"
                    name="bookedBy.role"
                    value={formData.bookedBy.role}
                    onChange={handleInputChange}
                    placeholder="e.g., Captain, Manager"
                    className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="bookedBy.email"
                    value={formData.bookedBy.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Mobile No
                  </label>
                  <input
                    type="text"
                    name="bookedBy.phone"
                    value={formData.bookedBy.phone}
                    onChange={handleInputChange}
                    placeholder="+1234567890"
                    className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Special requests or additional info"
                    rows="4"
                    className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full bg-gradient-to-r from-red-600 to-purple-600 text-white font-bold py-3 px-4 rounded-xl hover:from-red-700 hover:to-purple-700 transition-all transform hover:scale-[1.02] shadow-lg"
          >
            {loading ? "Submitting..." : "Submit Booking Request"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RentalFormSection;





// import { useEffect, useState } from "react";
// import axios from "axios";
// import toast from "react-hot-toast";
// import {
//   FaCalendarAlt,
//   FaCheckCircle,
//   FaRegCalendarCheck,
//   FaTimesCircle,
// } from "react-icons/fa";
// import { MdDateRange } from "react-icons/md";

// const RentalFormSection = ({
//   activeTab,
//   selectedDates,
//   formData,
//   setFormData,
//   fetchBookedEvents,
//   setSelectedDates,
// }) => {
//   const [loading, setLoading] = useState(false);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     if (name.includes(".")) {
//       const [parent, child] = name.split(".");
//       setFormData((prev) => ({
//         ...prev,
//         [parent]: { ...prev[parent], [child]: value },
//       }));
//     } else {
//       setFormData((prev) => ({ ...prev, [name]: value }));
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (selectedDates.length === 0) {
//       toast("Please select at least one date from the calendar.", {
//         position: "top-right",
//         autoClose: 5000,
//         style: {
//           background: "linear-gradient(135deg, #FF8A00, #FF3D00)",
//           color: "#fff",
//           fontWeight: "500",
//           fontFamily: "'Poppins', sans-serif",
//           borderRadius: "12px",
//           border: "1px solid #ff660040",
//           boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
//         },
//         icon: <FaCalendarAlt size={20} color="#fff" />,
//       });
//       return;
//     }
//     if (activeTab === "tournament" && selectedDates.length !== 2) {
//       toast(
//         "Please select exactly two dates (start and end) for a tournament.",
//         {
//           position: "top-right",
//           autoClose: 5000,
//           style: {
//             background: "linear-gradient(135deg, #FF8A00, #FF3D00)",
//             color: "#fff",
//             fontWeight: "500",
//             fontFamily: "'Poppins', sans-serif",
//             borderRadius: "12px",
//             border: "1px solid #ff660040",
//             boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
//           },
//           icon: <MdDateRange size={36} color="#fff" />,
//         }
//       );

//       return;
//     }

//     let submissionData = {
//       eventType: activeTab,
//       field: formData.field,
//       bookedBy: formData.bookedBy,
//       notes: formData.notes,
//       status: "pending",
//     };

//     if (activeTab === "single") {
//       submissionData.teamOne = formData.teamOne;
//       submissionData.teamTwo = formData.teamTwo;
//       submissionData.matchDate = selectedDates[0];
//     } else if (activeTab === "tournament") {
//       submissionData.numberOfMatches = formData.numberOfMatches;
//       submissionData.numberOfTeams = formData.numberOfTeams;
//       submissionData.dateRange = {
//         start: selectedDates[0],
//         end: selectedDates[1],
//       };
//     }

//     try {
//       setLoading(true);
//       const response = await axios.post(
//         `${process.env.REACT_APP_API_URL}/events/create-event`,
//         submissionData,
//         { headers: { "Content-Type": "application/json" } }
//       );
//       console.log("Booking Response:", response.data);
//       setSelectedDates([]);
//       setFormData({
//         eventType: "single",
//         field: "",
//         teamOne: "",
//         teamTwo: "",
//         numberOfMatches: "",
//         numberOfTeams: "",
//         bookedBy: {
//           name: "",
//           email: "",
//           phone: "",
//           organization: "",
//           address: "",
//           role: "",
//         },
//         notes: "",
//       });

//       fetchBookedEvents();

//       toast.success("Booking request submitted successfully!", {
//         position: "top-right",
//         autoClose: 4000,
//         hideProgressBar: false,
//         closeOnClick: true,
//         pauseOnHover: true,
//         draggable: true,
//         style: {
//           background: "linear-gradient(135deg, #050733, #0F9D58)",
//           color: "#FFFFFF",
//           fontWeight: "600",
//           fontFamily: "'Poppins', sans-serif",
//           border: "1px solid rgba(205, 27, 27, 0.4)",
//           borderRadius: "14px",
//           boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
//           letterSpacing: "0.3px",
//         },
//         icon: <FaCheckCircle size={22} color="#CD1B1B" />,
//       });
//     } catch (err) {
//       console.error("Error submitting booking:", err.response || err);
//       toast(
//         err?.response?.data?.error ||
//           "Failed to submit booking. Please try again.",
//         {
//           position: "top-right",
//           autoClose: 5000,
//           style: {
//             background: "linear-gradient(135deg, #FF8A00, #FF3D00)",
//             color: "#fff",
//             fontWeight: "500",
//             fontFamily: "'Poppins', sans-serif",
//             borderRadius: "12px",
//             border: "1px solid #ff660040",
//             boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
//           },
//           icon: <FaTimesCircle size={20} color="#fff" />,
//         }
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="w-full lg:w-3/5 flex justify-center">
//       <div className="bg-gradient-to-br from-[#150A2A] to-[#1E1235] p-6 md:p-8 rounded-2xl shadow-2xl w-full h-full flex flex-col">
//         <div className="text-center mb-6">
//           <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-red-400 to-purple-400 bg-clip-text text-transparent">
//             Schedule Your Rental
//           </h2>
//           <p className="text-gray-300 mt-2 text-sm">
//             Fill out your details below. We'll reach out to confirm your
//             booking.
//           </p>
//         </div>

//         <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
//           <div className="space-y-6 flex-1">
//             {/* Dynamic Fields */}
//             {activeTab === "single" ? (
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-300 mb-1">
//                     Team One
//                   </label>
//                   <input
//                     type="text"
//                     name="teamOne"
//                     value={formData.teamOne}
//                     onChange={handleInputChange}
//                     placeholder="Team name"
//                     className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
//                     required
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-300 mb-1">
//                     Team Two
//                   </label>
//                   <input
//                     type="text"
//                     name="teamTwo"
//                     value={formData.teamTwo}
//                     onChange={handleInputChange}
//                     placeholder="Team name"
//                     className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
//                     required
//                   />
//                 </div>
//               </div>
//             ) : (
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-300 mb-1">
//                     Number of Matches
//                   </label>
//                   <input
//                     type="number"
//                     name="numberOfMatches"
//                     value={formData.numberOfMatches}
//                     onChange={handleInputChange}
//                     placeholder="e.g., 5"
//                     min="1"
//                     className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
//                     required
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-300 mb-1">
//                     Number of Teams
//                   </label>
//                   <input
//                     type="number"
//                     name="numberOfTeams"
//                     value={formData.numberOfTeams}
//                     onChange={handleInputChange}
//                     placeholder="e.g., 8"
//                     min="2"
//                     className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
//                     required
//                   />
//                 </div>
//               </div>
//             )}

//             {/* Personal Info */}
//             <div className="pt-4 mt-4 border-t border-gray-700/30">
//               <h4 className="text-lg font-semibold mb-4 text-white">
//                 Your Information
//               </h4>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-300 mb-1">
//                     Full Name
//                   </label>
//                   <input
//                     type="text"
//                     name="bookedBy.name"
//                     value={formData.bookedBy.name}
//                     onChange={handleInputChange}
//                     placeholder="Your full name"
//                     className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
//                     required
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-300 mb-1">
//                     Your Role
//                   </label>
//                   <input
//                     type="text"
//                     name="bookedBy.role"
//                     value={formData.bookedBy.role}
//                     onChange={handleInputChange}
//                     placeholder="e.g., Captain, Manager"
//                     className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
//                     required
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-300 mb-1">
//                     Email
//                   </label>
//                   <input
//                     type="email"
//                     name="bookedBy.email"
//                     value={formData.bookedBy.email}
//                     onChange={handleInputChange}
//                     placeholder="your.email@example.com"
//                     className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
//                     required
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-300 mb-1">
//                     Mobile No
//                   </label>
//                   <input
//                     type="text"
//                     name="bookedBy.phone"
//                     value={formData.bookedBy.phone}
//                     onChange={handleInputChange}
//                     placeholder="+1234567890"
//                     className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
//                     required
//                   />
//                 </div>
//                 <div className="md:col-span-2">
//                   <label className="block text-sm font-medium text-gray-300 mb-1">
//                     Additional Notes
//                   </label>
//                   <textarea
//                     name="notes"
//                     value={formData.notes}
//                     onChange={handleInputChange}
//                     placeholder="Special requests or additional info"
//                     rows="4"
//                     className="w-full p-3 rounded-lg bg-[#1E1235] outline-none focus:ring-2 focus:ring-red-500 border border-gray-700 text-sm placeholder-gray-500"
//                   ></textarea>
//                 </div>
//               </div>
//             </div>
//           </div>

//           <button
//             type="submit"
//             disabled={loading}
//             className="mt-8 w-full bg-gradient-to-r from-red-600 to-purple-600 text-white font-bold py-3 px-4 rounded-xl hover:from-red-700 hover:to-purple-700 transition-all transform hover:scale-[1.02] shadow-lg"
//           >
//             {loading ? "Submitting..." : "Submit Booking Request"}
//           </button>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default RentalFormSection;